import { Component, type ReactNode } from "react";
import { isChunkLoadError } from "../chunk-load";

type ErrorBoundaryProps = {
  children: ReactNode;
  /** Clears a caught error when this value changes, e.g. the route path or active tab. */
  resetKey?: unknown;
  /** "page" replaces the page body; "panel" fits inside one section of a page. */
  variant?: "page" | "panel";
};

type ErrorBoundaryState = { failed: boolean; error: unknown; resetKey: unknown };

/**
 * Keeps one failing page or lazy panel from blanking the whole portal.
 *
 *   <ErrorBoundary resetKey={location.pathname}>…</ErrorBoundary>
 *   <ErrorBoundary variant="panel" resetKey={activeTab}><Suspense …><LazyPanel /></Suspense></ErrorBoundary>
 *
 * A missing code file after a deploy is explained as "a new version is available"
 * with a Reload action; any other error offers Reload and Try again.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = {
    failed: false,
    error: null,
    resetKey: this.props.resetKey,
  };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { failed: true, error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (Object.is(props.resetKey, state.resetKey)) return null;
    return { failed: false, error: null, resetKey: props.resetKey };
  }

  private readonly retry = () => this.setState({ failed: false, error: null });

  override render() {
    if (!this.state.failed) return this.props.children;
    const variant = this.props.variant ?? "page";
    const outdated = isChunkLoadError(this.state.error);
    const Heading = variant === "page" ? "h1" : "h2";
    return (
      <section className={`panel error-boundary error-boundary-${variant}`} role="alert">
        <p className="eyebrow">{outdated ? "Portal updated" : "Something went wrong"}</p>
        <Heading>
          {outdated
            ? "A new version of the portal is available"
            : variant === "page"
              ? "This page couldn’t be shown"
              : "This section couldn’t be shown"}
        </Heading>
        <p>
          {outdated
            ? "The portal was updated while this tab was open. Reload to continue with the latest version. Anything you already saved is safe."
            : "Reload to try again. Anything you already saved is safe. If it keeps happening, tell the workspace owner what you were doing."}
        </p>
        <div className="error-boundary-actions">
          <button className="button" type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
          {outdated ? null : (
            <button className="secondary-button" type="button" onClick={this.retry}>
              Try again
            </button>
          )}
        </div>
      </section>
    );
  }
}
